import { NodeType, Styleguide } from "../../api/types/styleguide.js";

export const DEFAULT_NAME = "Unnamed styleguide";

interface PageNode {
  id: number;
  url: string;
}

type HierarchicalNode = {
  [key: string]: PageNode | HierarchicalNode;
};

interface ConvertedTree {
  tree: HierarchicalNode;
}

function getUniqueName(
  baseName: string,
  existingKeys: Record<string, any>,
): string {
  if (!existingKeys[baseName]) {
    return baseName;
  }

  let counter = 1;
  while (existingKeys[`${baseName} ${counter}`]) {
    counter++;
  }
  return `${baseName} ${counter}`;
}

function processNode(node: NodeType, parent: HierarchicalNode) {
  node.children?.forEach((child) => {
    const childName = getUniqueName(child.name || child.type, parent);

    if (child.type === "page") {
      parent[childName] = { id: child.id, url: child.url };
    } else if (["category", "navigation"].includes(child.type)) {
      parent[childName] = parent[childName] || {};
      processNode(child, parent[childName] as HierarchicalNode);
    }
  });
}

export function formatTreeAsJson(tree: NodeType[]): Record<string, any> {
  const result: ConvertedTree = { tree: {} };

  tree.forEach((node) => {
    const baseName = getUniqueName(node.name || node.type, result.tree);

    result.tree[baseName] = result.tree[baseName] || {};
    processNode(node, result.tree[baseName] as HierarchicalNode);
  });

  return result.tree;
}

export interface StyleguideListItem {
  id: number;
  name: string;
}

export function formatStyleguideListAsJson(styleguides: Styleguide[]): {
  styleguides: StyleguideListItem[];
} {
  return {
    styleguides: styleguides.map((styleguide) => ({
      id: styleguide.id,
      name: styleguide.name ?? DEFAULT_NAME,
    })),
  };
}

export function formatStyleguideListAsMarkdown(
  styleguides: Styleguide[],
): string {
  return styleguides
    .map(
      (styleguide) =>
        `- ${styleguide.name ?? DEFAULT_NAME} (ID: ${styleguide.id})`,
    )
    .join("\n");
}
